import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { moneyAmountLabel } from '@/utils/formatCurrency';
import { adsApi, campaignsApi } from '@/api/client';
import { CampaignSearchPicker } from '@/components/CampaignSearchPicker';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { parseCampaignsPayload } from '@/utils/entityCrossFilter';
import { SmartBudgetWeekGrid } from '@/components/smartBudget/SmartBudgetWeekGrid';
import { profileHourlyBudgetsQueryKey } from '@/utils/hourlyBudgetList';
import {
  formatSmartBudgetInr,
  parseDailyBudgetInput,
  smartBudgetErrorMessage,
  smartBudgetPreviewSchedule,
  weeklyTotalFromDaily,
} from '@/utils/smartCampaignBudget';
import { cn } from '@/utils/cn';
import type { SmartBudgetPlan } from '@/types/smartBudget';
import type { SpCampaignNormalized } from '@/types/spCampaigns';

type SmartBudgetSectionProps = {
  profileId: string;
  onApplied?: () => void;
};

function AmountBars({
  items,
}: {
  items: { key: string; label: string; amount: number; equalShare: number; selected?: boolean; onSelect?: () => void }[];
}) {
  const max = Math.max(...items.map((item) => Math.max(item.amount, item.equalShare)), 1);
  return (
    <div className="flex items-end gap-2 overflow-x-auto pb-1">
      {items.map((item) => {
        const clickable = Boolean(item.onSelect);
        const inner = (
          <>
            <div className="flex h-36 w-8 flex-col justify-end gap-0.5">
              <div
                className="w-full rounded-t bg-brand"
                style={{ height: `${Math.max(4, (item.amount / max) * 100)}%` }}
                title={`Suggested ${formatSmartBudgetInr(item.amount)}`}
              />
            </div>
            <span className="mt-1 text-[11px] font-medium text-foreground">{item.label}</span>
            <span className="text-[10px] text-foreground-muted">{formatSmartBudgetInr(item.amount)}</span>
          </>
        );
        if (!clickable) {
          return (
            <div key={item.key} className="flex min-w-[3.25rem] flex-col items-center">
              {inner}
            </div>
          );
        }
        return (
          <button
            key={item.key}
            type="button"
            onClick={item.onSelect}
            className={cn(
              'flex min-w-[3.25rem] flex-col items-center rounded-lg px-1 py-1',
              item.selected ? 'bg-brand-subtle ring-1 ring-brand' : 'hover:bg-surface-muted'
            )}
          >
            {inner}
          </button>
        );
      })}
    </div>
  );
}

export function SmartBudgetSection({ profileId, onApplied }: SmartBudgetSectionProps) {
  const { showToast } = useToast();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const campaignFromUrl = searchParams.get('campaignId') ?? '';
  const [campaignId, setCampaignId] = useState(campaignFromUrl);
  const [budgetInput, setBudgetInput] = useState('');
  const [selectedDay, setSelectedDay] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const budgetPrefillFor = useRef('');

  const campaignsQ = useQuery({
    queryKey: ['smart-budget-campaigns', profileId],
    queryFn: async () => {
      const res = await adsApi.fetchProfileCampaigns(profileId, null, {
        status: 'ACTIVE',
      });
      if (!res.success) throw new Error('Failed to load campaigns');
      return parseCampaignsPayload(res.data).filter(
        (campaign) => campaign.state.toUpperCase() === 'ENABLED'
      );
    },
    enabled: Boolean(profileId),
  });

  const campaigns = campaignsQ.data ?? [];
  const selectedCampaign: SpCampaignNormalized | undefined = useMemo(
    () => campaigns.find((c) => c.campaignId === campaignId),
    [campaigns, campaignId]
  );

  useEffect(() => {
    if (campaignFromUrl) setCampaignId(campaignFromUrl);
  }, [campaignFromUrl]);

  useEffect(() => {
    if (!selectedCampaign || budgetPrefillFor.current === selectedCampaign.campaignId) return;
    budgetPrefillFor.current = selectedCampaign.campaignId;
    setBudgetInput(selectedCampaign.budget > 0 ? String(selectedCampaign.budget) : '');
  }, [selectedCampaign]);

  const parsedBudget = parseDailyBudgetInput(budgetInput);

  const previewM = useMutation({
    mutationFn: async () => {
      if (!campaignId) throw new Error('Select a campaign');
      if (parsedBudget.value == null) throw new Error(parsedBudget.error ?? 'Enter a daily budget');
      const res = await campaignsApi.previewSmartBudget(profileId, campaignId, {
        daily_budget: parsedBudget.value,
      });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to preview smart budget');
      return res.data;
    },
    onError: (error) => {
      showToast(smartBudgetErrorMessage(error), 'error');
    },
    onSuccess: (plan) => {
      const richest = [...plan.weekdays].sort((a, b) => b.amount - a.amount)[0];
      setSelectedDay(richest?.day_of_week ?? 0);
      setConfirming(false);
    },
  });

  const applyM = useMutation({
    mutationFn: async (plan: SmartBudgetPlan) => {
      const res = await campaignsApi.applySmartBudget(profileId, plan.campaign.campaign_id, {
        daily_budget: plan.daily_budget,
      });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to apply smart budget');
      return res.data;
    },
    onError: (error) => {
      showToast(smartBudgetErrorMessage(error), 'error');
    },
    onSuccess: async () => {
      showToast('Smart budget applied. Dayparting will pace Amazon daily budget from tonight.', 'success');
      await qc.invalidateQueries({ queryKey: profileHourlyBudgetsQueryKey(profileId) });
      setConfirming(false);
      onApplied?.();
    },
  });

  const plan = previewM.data ?? null;
  const previewSchedule = useMemo(
    () => (plan ? smartBudgetPreviewSchedule(plan.hourly_budget_payload) : null),
    [plan]
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Smart budget</h2>
        <p className="mt-1 max-w-3xl text-body-sm text-foreground-muted">
          Pick one campaign that has been running for at least four weeks and a daily budget. We
          split the weekly pool ({budgetInput && parsedBudget.value != null ? formatSmartBudgetInr(weeklyTotalFromDaily(parsedBudget.value)) : 'daily × 7'})
          across weekdays and IST hours from past performance (each weekday stays within 25% of the
          daily budget), then apply it as a weekly dayparting schedule.
        </p>
      </div>

      <Card className="p-4">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_12rem_auto] md:items-end">
          <label className="block">
            <span className="mb-1 block text-body-sm font-medium text-foreground">Campaign</span>
            <CampaignSearchPicker
              options={campaigns}
              value={campaignId}
              onChange={(id) => {
                setCampaignId(id);
                previewM.reset();
                setConfirming(false);
              }}
              loading={campaignsQ.isLoading}
              disabled={previewM.isPending || applyM.isPending}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-body-sm font-medium text-foreground">{moneyAmountLabel('Daily budget')}</span>
            <Input
              inputMode="decimal"
              value={budgetInput}
              onChange={(e) => {
                setBudgetInput(e.target.value);
                previewM.reset();
                setConfirming(false);
              }}
              placeholder="2000"
              disabled={previewM.isPending || applyM.isPending}
            />
          </label>
          <Button
            onClick={() => previewM.mutate()}
            disabled={!campaignId || parsedBudget.value == null || previewM.isPending}
          >
            {previewM.isPending ? <LoaderLabel /> : 'Preview plan'}
          </Button>
        </div>
        {parsedBudget.error && budgetInput.trim() ? (
            <p className="mt-2 text-body-sm text-error">{parsedBudget.error}</p>
        ) : null}
        {campaignsQ.error ? (
          <p className="mt-2 text-body-sm text-error">{smartBudgetErrorMessage(campaignsQ.error)}</p>
        ) : null}
      </Card>

      {previewM.isPending ? (
        <div className="flex items-center gap-2 text-body-sm text-foreground-muted">
          <Spinner className="h-4 w-4" />
          Building a 4-week plan…
        </div>
      ) : null}

      {plan ? (
        <div className="space-y-4">
          <Card className="p-4">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <h3 className="font-semibold text-foreground">Weekday investment</h3>
                <p className="text-body-sm text-foreground-muted">
                  {formatSmartBudgetInr(plan.daily_budget)} / day → {formatSmartBudgetInr(plan.weekly_total)} / week
                  · {plan.lookback.start} to {plan.lookback.end}
                  · {plan.active_days} active days
                </p>
              </div>
              {plan.campaign.campaign_type !== 'SP' ? (
                <span className="text-body-sm text-foreground-muted">Hours are even for {plan.campaign.campaign_type}</span>
              ) : null}
            </div>
            <AmountBars
              items={plan.weekdays.map((day) => ({
                key: String(day.day_of_week),
                label: day.day_name.slice(0, 3),
                amount: day.amount,
                equalShare: day.equal_share,
                selected: day.day_of_week === selectedDay,
                onSelect: () => setSelectedDay(day.day_of_week),
              }))}
            />
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-left text-body-sm">
                <thead>
                  <tr className="text-foreground-muted">
                    <th className="py-1 pr-3 font-medium">Day</th>
                    <th className="py-1 pr-3 font-medium">Suggested</th>
                    <th className="py-1 pr-3 font-medium">Equal share</th>
                    <th className="py-1 pr-3 font-medium">Sales</th>
                    <th className="py-1 pr-3 font-medium">ROAS</th>
                    <th className="py-1 font-medium">Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.weekdays.map((day) => (
                    <tr key={day.day_of_week} className={day.day_of_week === selectedDay ? 'bg-brand-subtle' : ''}>
                      <td className="py-1 pr-3">{day.day_name}</td>
                      <td className="py-1 pr-3 font-medium">{formatSmartBudgetInr(day.amount)}</td>
                      <td className="py-1 pr-3 text-foreground-muted">{formatSmartBudgetInr(day.equal_share)}</td>
                      <td className="py-1 pr-3">{formatSmartBudgetInr(day.sales)}</td>
                      <td className="py-1 pr-3">{day.roas.toFixed(2)}</td>
                      <td className="py-1">{Math.round(day.clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {plan && previewSchedule ? (
            <Card className="p-4">
              <h3 className="font-semibold text-foreground">Hourly unlocks · IST</h3>
              <p className="mb-3 text-body-sm text-foreground-muted">
                Midnight sets Amazon’s daily cap; later hours add budget. This is the weekly
                dayparting schedule that will be applied.
              </p>
              <SmartBudgetWeekGrid
                slotsByDay={previewSchedule.slotsByDay}
                amountsByDay={previewSchedule.amountsByDay}
                weekdays={plan.weekdays}
                selectedDay={selectedDay}
              />
            </Card>
          ) : null}

          {plan.warnings.length > 0 ? (
            <ul className="list-disc space-y-1 pl-5 text-body-sm text-foreground-muted">
              {plan.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}

          {plan.in_hourly_budget_group ? (
            <p className="text-body-sm text-error">
              This campaign is in a dayparting group. Remove it from the group before applying.
            </p>
          ) : confirming ? (
            <Card className="space-y-3 p-4">
              <p className="text-body-sm text-foreground">
                Apply this weekly schedule to {plan.campaign.name || plan.campaign.campaign_id}?{' '}
                {plan.has_existing_hourly_budget
                  ? 'This replaces the existing dayparting config for this campaign.'
                  : 'The hourly budget cron will pace Amazon’s daily budget from the next IST midnight.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => applyM.mutate(plan)} disabled={applyM.isPending}>
                  {applyM.isPending ? <LoaderLabel /> : 'Confirm apply'}
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(false)} disabled={applyM.isPending}>
                  Cancel
                </Button>
              </div>
            </Card>
          ) : (
            <Button onClick={() => setConfirming(true)} disabled={applyM.isPending}>
              Apply to dayparting
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function LoaderLabel() {
  return (
    <>
      <Spinner className="h-4 w-4" />
      Working…
    </>
  );
}
